/* eslint-disable */
import React, { PureComponent } from 'react';
import { Route, Switch, Redirect, withRouter } from 'react-router';
import { ConnectedRouter } from 'connected-react-router';
import Loadable from 'react-loadable';
import { connect } from 'react-redux';
import Loader from './components/LayoutComponents/Loader';
import NotFound from './pages/404';
import IndexLayout from './layouts';
import { logPageView } from './analytics';
import { FEATURES, RouteAccessGuard } from './rbac';
// getIPDetails (services/ipapi) and currentAccount (services/auth) are loaded
// lazily at their lifecycle call sites — services/auth.js's transitive import
// of the Parse SDK (~193 KB raw) was the second of two synchronous chains
// pulling Parse into main.js; the first was the saga registry (now lazy via
// configureStore.js bootstrapSaga).

const loadable = (loader) =>
  Loadable({
    loader,
    delay: false,
    loading: () => <Loader />,
  });

// RBAC: maps a route path to the FEATURE that gates its `view` access. Kept here
// (keyed by the exact `path` strings below) rather than on each route object so the
// routes array stays untouched. Any path NOT listed here is un-gated by RBAC — that's
// intentional for Auth pages and the "Other" utility routes (/blank, /inspection,
// /contact/contact-info). generateRoute() wraps a route in <RouteAccessGuard> when its
// path resolves to a feature here.
const ROUTE_FEATURE = {
  // Dashboard
  '/dashboard': FEATURES.DASHBOARD,
  '/dashboard/task-panel': FEATURES.DASHBOARD,
  '/dashboard/change/:booking_id/:trip_id': FEATURES.DASHBOARD,
  '/dashboard/bookings-view/:id': FEATURES.DASHBOARD,

  // Reports
  '/reports/graphs': FEATURES.REPORTS,
  '/reports/trends': FEATURES.REPORTS,
  '/reports/mt-analytics': FEATURES.REPORTS,
  '/reports/history/changePaymentStatus/:booking_id': FEATURES.REPORTS,
  '/reports/map': FEATURES.REPORTS,
  '/reports/history': FEATURES.REPORTS,

  // Fleet
  '/fleet/vehicles': FEATURES.VEHICLES,
  '/fleet/drivers/list': FEATURES.DRIVERS,
  '/fleet/drivers/schedule': FEATURES.DRIVER_SHIFT,
  '/fleet/drivers/report': FEATURES.SHIFT_REPORT,
  '/fleet/drivers': FEATURES.DRIVERS,
  '/fleet/trips': FEATURES.TRIPS,
  '/fleet/dispatch': FEATURES.DISPATCH_BOARD,
  '/fleet/dispatch/legacy': FEATURES.DISPATCH_BOARD,
  '/fleet/dispatchtemplate': FEATURES.DISPATCH_TEMPLATE,
  '/fleet/dispatchtemplate/view/templateId/:id': FEATURES.DISPATCH_TEMPLATE,
  '/fleet/inspection': FEATURES.FLEET_INSPECTION,

  // Rider services
  '/rider-services/reservations': FEATURES.RESERVATIONS,
  '/rider-services/bookings': FEATURES.BOOKINGS,
  '/rider-services/bookings/futurebookinglist': FEATURES.BOOKINGS,
  '/rider-services/bookings/prepareNew': FEATURES.BOOKINGS,
  '/rider-services/bookings/newBooking': FEATURES.BOOKINGS,
  '/rider-services/bookings/assess': FEATURES.BOOKINGS,
  '/rider-services/bookings/view/:id/': FEATURES.BOOKINGS,
  '/rider-services/bookings/change/:id': FEATURES.BOOKINGS,
  '/rider-services/bookings/changePaymentStatus/:booking_id': FEATURES.BOOKINGS,
  '/rider-services/subscription': FEATURES.SUBSCRIPTION,
  '/rider-services/nem-transport': FEATURES.NEM_TRANSPORT,
  '/rider-services/broker-integration': FEATURES.BROKER_INTEGRATION,
  '/rider-services/members': FEATURES.MEMBERS,
  '/rider-services/members/prepareNew': FEATURES.MEMBERS,
  '/rider-services/members/view/:id': FEATURES.MEMBERS,
  '/rider-services/members/modify/:user_id': FEATURES.MEMBERS,
  '/rider-services/riders': FEATURES.RIDERS,
  '/rider-services/notifications': FEATURES.NOTIFICATIONS,

  // Programs
  '/programs/services': FEATURES.PROGRAMS_SERVICES,
  '/programs/accounts': FEATURES.PROGRAMS_ACCOUNTS,
  '/programs/pricing': FEATURES.PRICING,

  // Account management
  '/account_mngm/users': FEATURES.ACCOUNT_USERS,
  '/account_mngm/plan-billing': FEATURES.ACCOUNT_BILLING,
  '/account_mngm/payment-method': FEATURES.ACCOUNT_PAYMENT,
  '/account_mngm/payment-method/stripe-payment': FEATURES.ACCOUNT_PAYMENT,
};

const routes = [
  // Auth pages
  {
    path: '/auth/login',
    component: loadable(() => import('./pages/auth/login/index')),
    privilege: [],
    exact: true,
  },
  {
    path: '/auth/signup',
    // component: Signup,
    component: loadable(() => import('./pages/auth/signup')),
    privilege: [],
    exact: true,
  },
  {
    path: '/auth/forgotpassword',
    // component: Signup,
    component: loadable(() => import('./pages/auth/forgotPassword')),
    privilege: [],
    exact: true,
  },

  // OAuth2 "Login with Duet" SSO consent page (public, no auth session involved)
  {
    path: '/oauth/authorize',
    component: loadable(() => import('./pages/oauth/authorize')),
    privilege: [],
    exact: true,
  },

  // Dashboard
  {
    path: '/dashboard',
    component: loadable(() => import('./pages/dashboard')),
    privilege: [1, 3],
    exact: true,
  },
  {
    path: '/dashboard/task-panel',
    component: loadable(() => import('./pages/dashboard/taskPanel')),
    privilege: [1, 3],
    exact: true,
  },
  {
    path: '/dashboard/change/:booking_id/:trip_id',
    component: loadable(() => import('./pages/dashboard/change')),
    privilege: [1, 3],
    exact: true,
  },
  {
    path: '/dashboard/bookings-view/:id',
    component: loadable(() => import('./pages/dashboard/viewBookingInfo')),
    privilege: [1, 3],
    exact: true,
  },
  {
    path: '/reports/graphs',
    component: loadable(() => import('./pages/reports')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/reports/trends',
    component: loadable(() => import('./pages/trend-reports')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/reports/mt-analytics',
    component: loadable(() => import('./pages/mt-analytics')),
    privilege: [3, 4],
    exact: true,
  },
  // {
  //   path: "/reports/history/view/:id",
  //   component: loadable(() => import("./pages/history/viewBookingInfo")),
  //   privilege: [3, 4],
  //   exact: true
  // },
  {
    path: '/reports/history/changePaymentStatus/:booking_id',
    component: loadable(() => import('./pages/history/changePaymentStatus')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/reports/map',
    component: loadable(() => import('./pages/odmapdisplay')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/reports/history',
    component: loadable(() => import('./pages/history')),
    privilege: [3, 4],
    exact: true,
  },
  // {
  //   path: '/reports/pdfviewer',
  //   component: loadable(() => import('./pages/pdf-viewer')),
  //   privilege: [3, 4],
  //   exact: true,
  // },
  {
    path: '/blank',
    component: loadable(() => import('./pages/blank')),
    privilege: [],
    exact: true,
  },
  // Inspection (embedded in mobile app, no auth)
  {
    path: '/inspection',
    component: loadable(() => import('./pages/inspection')),
    privilege: [],
    exact: true,
  },
  // Fleet/Vehicles
  {
    path: '/fleet/vehicles',
    component: loadable(() => import('./pages/fleet/vehicles')),
    privilege: [3, 4],
    exact: false,
  },
  // Fleet/drivers
  {
    path: '/fleet/drivers/list',
    component: loadable(() => import('./pages/fleet/drivers')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/drivers/schedule',
    component: loadable(() => import('./pages/fleet/drivers/schedule')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/drivers/report',
    component: loadable(() => import('./pages/fleet/drivers/ShiftReport')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/drivers',
    component: loadable(() => import('./pages/fleet/drivers')),
    privilege: [3, 4],
    exact: false,
  },
  {
    path: '/fleet/trips',
    component: loadable(() => import('./pages/fleet/trips')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/dispatch',
    component: loadable(() => import('./pages/fleet/drivers/DispatchBoard/')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/dispatch/legacy',
    component: loadable(() => import('./pages/fleet/drivers/dispatch-old')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/dispatchtemplate',
    component: loadable(() => import('./pages/fleet/drivers/templateIndex')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/fleet/dispatchtemplate/view/templateId/:id',
    component: loadable(() => import('./pages/fleet/drivers/savedTemplateDispatch')),
    privilege: [3, 4],
    exact: true,
  },

  // rider service

  {
    path: '/rider-services/reservations',
    component: loadable(() => import('./pages/rider-services/reservations')),
    privilege: [1, 2],
    exact: false,
  },
  {
    path: '/rider-services/bookings',
    component: loadable(() => import('./pages/rider-services/bookings')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/subscription',
    component: loadable(() => import('./pages/rider-services/subscription')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/rider-services/bookings/futurebookinglist',
    component: loadable(() => import('./pages/rider-services/bookings/futureBookingList')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/bookings/prepareNew',
    component: loadable(() => import('./pages/rider-services/bookings/PrepareNewPage')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/bookings/newBooking',
    component: loadable(() => import('./pages/rider-services/bookings/newBooking')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/bookings/assess',
    component: loadable(() => import('./pages/rider-services/bookings/assess')),
    privilege: [1, 2],
    exact: true,
  },
  // {
  //   path: '/rider-services/bookings/assess/first-trip',
  //   component: loadable(() => import('./pages/rider-services/bookings/firsttrip')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  // NEM Transport /rider-services/nem-transport/newInsurance
  {
    path: '/rider-services/nem-transport',
    component: loadable(() => import('./pages/rider-services/nem-transport')),
    privilege: [1, 2],
    exact: true,
  },

  // {
  //   path: '/rider-services/nem-transport/select',
  //   component: loadable(() => import('./pages/rider-services/nem-transport')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  // {
  //   path: '/rider-services/nem-transport/newInsurance',
  //   component: loadable(() => import('./pages/rider-services/nem-transport/newInsurance')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  // {
  //   path: '/rider-services/nem-transport/fileUpload',
  //   component: loadable(() => import('./pages/rider-services/nem-transport/fileUpload')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  {
    path: '/rider-services/broker-integration',
    component: loadable(() => import('./pages/rider-services/broker-integration/')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/fleet/inspection',
    component: loadable(() => import('./pages/fleet/inspections')),
    privilege: [1, 2],
    exact: true,
  },

  // {
  //   path: "/rider-services/bookings/prepareNew",
  //   component: loadable(() =>
  //     import("./pages/rider-services/bookings/prepareNew")
  //   ),
  //   exact: true
  // },
  {
    exact: true,
    path: '/rider-services/bookings/view/:id/',
    component: loadable(() => import('./pages/rider-services/bookings/viewBookingInfo')),
    privilege: [1, 2],
  },
  // {
  //   path: '/rider-services/bookings/newsubscription/:id',
  //   component: loadable(() => import('./pages/rider-services/bookings/NewSubscription')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  {
    path: '/rider-services/bookings/change/:id',
    component: loadable(() => import('./pages/rider-services/bookings/prepareChange')),
    privilege: [1, 2],
    exact: true,
  },
  // {
  //   path: '/rider-services/bookings/modify/:booking_id/:passenger_id/:historySource?',
  //   component: loadable(() => import('./pages/rider-services/bookings/modifyBookingInfo')),
  //   privilege: [1, 2],
  //   exact: true,
  // },
  {
    path: '/rider-services/bookings/changePaymentStatus/:booking_id',
    component: loadable(() => import('./pages/rider-services/bookings/changePaymentStatus')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/members',
    component: loadable(() => import('./pages/rider-services/members')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/members/prepareNew',
    component: loadable(() => import('./pages/rider-services/members/prepareNew')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/members/view/:id',
    component: loadable(() => import('./pages/rider-services/members/viewMemberInfo')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/members/modify/:user_id',
    component: loadable(() => import('./pages/rider-services/members/modifyMemberInfo')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/riders',
    component: loadable(() => import('./pages/rider-services/riders')),
    privilege: [1, 2],
    exact: true,
  },
  {
    path: '/rider-services/notifications',
    component: loadable(() => import('./pages/rider-services/notifications')),
    privilege: [1, 2],
    exact: true,
  },
  // programs/services
  {
    path: '/programs/services',
    component: loadable(() => import('./pages/programs/services')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/programs/accounts',
    component: loadable(() => import('./pages/programs/accounts')),
    privilege: [3, 4],
    exact: true,
  },
  // Account Management

  {
    path: '/programs/pricing',
    component: loadable(() => import('./pages/programs/pricing')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/account_mngm/users',
    component: loadable(() => import('./pages/account-mngm/users')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/account_mngm/plan-billing',
    component: loadable(() => import('./pages/account-mngm/billing')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/account_mngm/payment-method',
    component: loadable(() => import('./pages/account-mngm/payment')),
    privilege: [3, 4],
    exact: true,
  },
  {
    path: '/account_mngm/payment-method/stripe-payment',
    component: loadable(() => import('./pages/account-mngm/payment/stripePayment')),
    privilege: [3, 4],
    exact: true,
  },
  // contact info
  {
    path: '/contact/contact-info',
    component: loadable(() => import('./pages/contact')),
    privilege: [],
    exact: true,
  },
  // help center
  {
    path: '/help',
    component: loadable(() => import('./pages/help')),
    privilege: [],
    exact: true,
  },
];

@connect(({ auth }) => ({ auth }))
class Router extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      startTime: Date.now(),
    };
  }

  async componentDidMount() {
    const env = window._env_.DEPLOYMENT_PROFILE;

    // Skip geo-redirect for embedded pages
    if (window.location.pathname.startsWith('/inspection')) return;

    try {
      if (env !== 'dev' && env !== 'preDev') {
        const { default: getIPDetails } = await import(/* webpackChunkName: "ipapi" */ './services/ipapi');
        const { data } = await getIPDetails();
        if (data) {
          const { longitude, latitude, country_code } = data;
          if (country_code.toUpperCase() === 'US') {
            if (
              (latitude > 37.001716 || latitude < 37.001716) &&
              longitude < -94.618903 &&
              !window.location.href.startsWith('https://usnorthwest.duetride.com') &&
              !window.location.href.startsWith('https://duetride.com') &&
              !window.location.href.startsWith('https://duetride.io') &&
              !window.location.href.startsWith('https://predev.duetride.io')
            ) {
              window.location.href = 'https://usnorthwest.duetride.com/';
            } else if (
              (latitude > 37.001716 || latitude < 37.001716) &&
              longitude > -94.618903 &&
              !window.location.href.startsWith('https://duetride.com') &&
              !window.location.href.startsWith('https://duetride.io') &&
              !window.location.href.startsWith('https://predev.duetride.io')
            ) {
              window.location.href = 'https://duetride.com/';
            }
          } else if (
            !window.location.href.startsWith('https://apacaus.duetride.com') &&
            !window.location.href.startsWith('https://duetride.io') &&
            !window.location.href.startsWith('https://predev.duetride.io') &&
            !window.location.href.startsWith('https://localhost')
          ) {
            window.location.href = 'https://apacaus.duetride.com/';
          }
        }
      }
    } catch (e) {
      console.error(e);
    }
  }

  componentDidUpdate(prevProps) {
    const logUserPageView = async (pageViewStartTime, pageViewEndTime) => {
      try {
        const { currentAccount } = await import(/* webpackChunkName: "auth-service" */ './services/auth');
        const user = await currentAccount();
        logPageView(user?.id, pageViewEndTime - pageViewStartTime);
      } catch (e) {
        if (e.message !== 'Not logged in.') {
          console.error(e);
        }
      }
    };

    if (this.props.location !== prevProps.location) {
      // Log the page view on route change
      const { startTime } = this.state;
      const endTime = Date.now();
      logUserPageView(startTime, endTime);
      this.setState({ startTime: endTime });
    }
  }

  // Builds the <Route>, wrapping the component in the RBAC guard when the path maps to
  // a feature (see ROUTE_FEATURE). Un-gated paths render exactly as before.
  buildRoute = (route) => {
    const feature = ROUTE_FEATURE[route.path];

    if (!feature) {
      return <Route path={route.path} component={route.component} key={route.path} exact={route.exact} />;
    }

    const RouteComponent = route.component;
    return (
      <Route
        path={route.path}
        key={route.path}
        exact={route.exact}
        render={(props) => (
          <RouteAccessGuard feature={feature}>
            <RouteComponent {...props} />
          </RouteAccessGuard>
        )}
      />
    );
  };

  generateRoute = (route) => {
    const { auth } = this.props;

    if (!auth.authorized || route.privilege.length === 0) return this.buildRoute(route);

    for (let index = 0; index < route.privilege.length; index++) {
      const element = route.privilege[index];
      if (auth.is_admin && auth.is_admin.includes(element)) {
        return this.buildRoute(route);
      }
    }
  };

  render() {
    const { history, auth } = this.props;

    return (
      <ConnectedRouter history={history}>
        <IndexLayout>
          <Switch>
            <Route exact path="/" key="/" render={() => <Redirect to="/dashboard" />} />
            {routes.map((route) => this.generateRoute(route))}

            {/* <Route path="/reports/map" component={ODMapPage}  /> */}
            {/* <Route path="/reports/history" component={HistoryDisplay} /> */}
            {/* <Route path="/reports/history/view/:id" component={bookingHistoryView} /> */}

            <Route component={NotFound} />
          </Switch>
        </IndexLayout>
      </ConnectedRouter>
    );
  }
}

export default withRouter(Router);
